import React, { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import YoutubePlayer from "react-native-youtube-iframe";
import { CF } from "../../theme/turquoiseTheme.js";
import { getBottomInset, getTopInset } from "../../utils/safeInsets.js";
import { extractYoutubeVideoId, youtubeWatchUrl } from "../../utils/youtube.js";
import { useLanguage } from "../../i18n/LanguageContext.js";
import { touchUI } from "../../utils/touchUI.js";

const TOP = getTopInset();
const BOTTOM = getBottomInset();

/**
 * Plays YouTube inside Procsystim on the phone (IFrame player).
 * Framing is minimal — goal is reliable in-app playback.
 *
 * @param {{
 *   visible: boolean,
 *   title: string,
 *   channel?: string,
 *   url: string,
 *   onClose: () => void,
 * }} props
 */
export function YouTubePlayerModal({ visible, title, channel, url, onClose }) {
  const { t } = useLanguage();
  const { width } = useWindowDimensions();
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  const videoId = useMemo(() => extractYoutubeVideoId(url), [url]);
  const playerWidth = Math.min(width - 32, 560);
  const playerHeight = Math.round((playerWidth * 9) / 16);

  const openOnYoutube = useCallback(() => {
    if (!videoId) return;
    void Linking.openURL(youtubeWatchUrl(videoId));
  }, [videoId]);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      onShow={() => {
        setReady(false);
        setFailed(false);
      }}
    >
      <View style={[styles.root, { paddingTop: TOP, paddingBottom: BOTTOM }]}>
        <Pressable onPress={onClose} hitSlop={touchUI.hitSlop} style={styles.backBtn}>
          <Text style={styles.backBtnText}>{t("library_back")}</Text>
        </Pressable>

        <Text style={styles.title} numberOfLines={3}>
          {title}
        </Text>
        {channel ? <Text style={styles.channel}>{channel}</Text> : null}

        {videoId && !failed ? (
          <View style={[styles.playerWrap, { height: playerHeight }]}>
            <YoutubePlayer
              key={videoId}
              height={playerHeight}
              width={playerWidth}
              videoId={videoId}
              play={false}
              webViewProps={{
                allowsFullscreenVideo: true,
                mediaPlaybackRequiresUserAction: false,
                androidLayerType: "hardware",
              }}
              initialPlayerParams={{
                controls: true,
                modestbranding: true,
                rel: false,
                preventFullScreen: false,
              }}
              onReady={() => setReady(true)}
              onError={() => setFailed(true)}
            />
            {!ready ? (
              <View style={styles.loading} pointerEvents="none">
                <ActivityIndicator color="#fff" size="large" />
                <Text style={styles.loadingText}>{t("video_loading")}</Text>
              </View>
            ) : null}
          </View>
        ) : (
          <View style={styles.failed}>
            <Text style={styles.failedText}>{t("video_unavailable")}</Text>
            {videoId ? (
              <Pressable style={styles.failedBtn} onPress={openOnYoutube}>
                <Text style={styles.failedBtnText}>{t("video_open_youtube")}</Text>
              </Pressable>
            ) : null}
          </View>
        )}

        {videoId ? (
          <Pressable style={styles.ytLink} onPress={openOnYoutube}>
            <Text style={styles.ytLinkText}>{t("video_open_youtube")}</Text>
          </Pressable>
        ) : null}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: "#061018", paddingHorizontal: 16 },
  backBtn: {
    alignSelf: "flex-start",
    minHeight: touchUI.minHit,
    minWidth: touchUI.backMinWidth,
    paddingVertical: touchUI.backPadV,
    paddingHorizontal: touchUI.backPadH,
    marginBottom: 8,
    justifyContent: "center",
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.08)",
  },
  backBtnText: { color: CF.cyan, fontWeight: "800", fontSize: touchUI.backLabel },
  title: { color: "#FFFFFF", fontSize: 20, fontWeight: "900", marginBottom: 4 },
  channel: { color: CF.cyan, fontWeight: "700", fontSize: 13, marginBottom: 14 },
  playerWrap: {
    width: "100%",
    borderRadius: 14,
    overflow: "hidden",
    backgroundColor: "#000",
    alignItems: "center",
  },
  loading: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.55)",
  },
  loadingText: { color: "#E2E8F0", fontWeight: "700", marginTop: 10 },
  failed: {
    minHeight: 180,
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    backgroundColor: "#0F172A",
    borderRadius: 14,
  },
  failedText: { color: "#E2E8F0", fontWeight: "700", textAlign: "center", marginBottom: 12 },
  failedBtn: {
    backgroundColor: CF.cyan,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
  },
  failedBtnText: { color: "#042F2E", fontWeight: "800" },
  ytLink: {
    marginTop: 18,
    alignSelf: "center",
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  ytLinkText: { color: CF.primaryBright, fontWeight: "800", fontSize: 14 },
});
